import { useState } from "react";
import { Check, Share2 } from "lucide-react";
import { toast } from "sonner";

interface ConversationShareButtonProps {
  conversationId: string;
}

/**
 * Copy the conversation URL to clipboard. Only mounts on the full-page
 * route (the side modal uses the "View full page" pill instead).
 *
 * Outline-style button so it sits beside the heavy gradient Analyze /
 * Mark Reviewed action set without competing visually. guidelines/design.md §7
 * "brand-tinted soft button" pattern, adapted with the HG gradient on
 * the icon.
 */
export function ConversationShareButton({ conversationId }: ConversationShareButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleClick = async () => {
    const url = `${window.location.origin}/genie-hub/conversations/${conversationId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied. Send it to a teammate.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Try the address bar.");
    }
  };

  return (
    <button
      onClick={handleClick}
      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold border bg-white hover:bg-gradient-to-r hover:from-[#4E9CFF]/8 hover:to-[#FF4DB8]/8 transition-colors"
      style={{
        borderColor: "rgba(78,156,255,0.3)",
      }}
      title="Copy a link to this conversation"
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 text-[#4E9CFF]" />
      ) : (
        <Share2 className="w-3.5 h-3.5 text-[#FF4DB8]" />
      )}
      <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
        {copied ? "Copied" : "Share"}
      </span>
    </button>
  );
}
